/**
 * Handles all the messages that e have dismissed.
 *
 * Some messageges are not important and we want to dismiss them forever, but others we want to show again
 */

import { useEventStoreRef, useUserStoreRef } from '@/stores';
import { defineStore, storeToRefs } from 'pinia';
import { computed, onMounted, ref, watch } from 'vue';
import { DismissedMessagesSettings } from '../settings/DismissedMessagesSettings';
import { useStoredSettings } from '../settings';
import { DismissedMessage } from './DismissedMessage';

export const useDismissedMessages = defineStore('dismissedmessages', () => {
    const _dismissedMessages = ref<DismissedMessage[]>([]);

    const _globalDismissedMessages = ref<DismissedMessage[]>([]);

    const isLoaded = ref(false);

    // A reference to the eventID so we can clear the messages for the old eventID.
    const { eventID } = useEventStoreRef();

    const { user } = useUserStoreRef();

    /**
     * A list of all the dismissed messages
     */
    const computedDismissedMessages = computed(() => {
        return !isLoaded.value ? null : _dismissedMessages.value.concat(_globalDismissedMessages.value)?.map((m) => m.key);
    });

    /**
     * Dismiss a message for a specific key for a specific amount of hours. if no hours are provided, the message will be dismissed forever.
     * @param key
     * @param dismissedforhours
     * @param global If the message is global, it will show independent of the eventID
     */
    const dismissMessage = (key: string, dismissedforhours?: number | undefined, global: boolean = false) => {
        const dismissedUntil: Date | undefined = dismissedforhours
            ? new Date(new Date().getTime() + dismissedforhours * 60 * 60 * 1000)
            : undefined;

        // Add the message to the dismissed messages
        if (global) {
            const exists = _globalDismissedMessages.value.find((m) => m.key === key);
            if (exists) {
                exists.dismissedUntil = dismissedUntil;
            } else {
                _globalDismissedMessages.value.push(new DismissedMessage(key, dismissedUntil));
            }

            _globalDismissedMessages.value = _globalDismissedMessages.value.filter(
                (m) => m.dismissedUntil === undefined || m.dismissedUntil > new Date(),
            );
        } else {
            const exists = _dismissedMessages.value.find((m) => m.key === key);
            if (exists) {
                exists.dismissedUntil = dismissedUntil;
            } else {
                _dismissedMessages.value.push(new DismissedMessage(key, dismissedUntil));
            }

            _dismissedMessages.value = _dismissedMessages.value.filter(
                (m) => m.dismissedUntil === undefined || m.dismissedUntil > new Date(),
            );
        }

        // Save the dismissed messages
        saveDismissedMessages();
    };

    /**
     * Load the dismissed messages from the json storage
     * @param newid
     * @param oldid
     */
    const loadDismissedMessages = async (newid: number, oldid?: number) => {
        if (!user.value || user.value?.ID == 0) {
            return;
        }

        // Load the dismissed messages from the json storage
        // TODO: Delete all the messages
        if (eventID.value > 0) {
            _dismissedMessages.value = (
                (await useStoredSettings().GetSettings(DismissedMessagesSettings, eventID.value, user.value?.ID)) ??
                new DismissedMessagesSettings()
            )?.dismissedMessages
                ?.map((x) => new DismissedMessage(x.key, x.dismissedUntil))
                ?.filter((m) => !m.dismissedUntil || m.dismissedUntil > new Date());
        } else {
            _dismissedMessages.value = [];
        }

        // Load the global dismissed messages
        _globalDismissedMessages.value = (
            (await useStoredSettings().GetSettings(DismissedMessagesSettings, null, user.value?.ID)) ?? new DismissedMessagesSettings()
        )?.dismissedMessages
            ?.map((x) => new DismissedMessage(x.key, x.dismissedUntil))
            ?.filter((m) => !m.dismissedUntil || m.dismissedUntil > new Date());

        isLoaded.value = true;
    };

    /**
     * We save the dismissed messages in the json storage and clear them after 60 days.
     * This mean that if we open an event after 60 days, we migt see the messages again.
     */
    const saveDismissedMessages = async () => {
        // We clear the settings after 60 days.
        const date60days = new Date(new Date().getTime() + 60 * 24 * 60 * 60 * 1000);

        // We store the dismissed messages for the current eventID and user
        if (eventID.value > 0 && user.value != undefined && user.value?.ID != undefined && user.value?.ID > 0) {
            const dismissedMessagesSettings = new DismissedMessagesSettings();
            dismissedMessagesSettings.dismissedMessages = _dismissedMessages.value;
            await useStoredSettings().SaveSettings(
                DismissedMessagesSettings,
                dismissedMessagesSettings,
                eventID.value,
                user.value?.ID,
                null,
                date60days,
            );
        }

        const dismissedMessagesSettingsGlobal = new DismissedMessagesSettings();
        dismissedMessagesSettingsGlobal.dismissedMessages = _globalDismissedMessages.value;

        // We store the dismissed messages for the global user
        // This mean it will store the type but only with user id
        await useStoredSettings().SaveSettings(
            DismissedMessagesSettings,
            dismissedMessagesSettingsGlobal,
            null,
            user.value?.ID,
            null,
            date60days,
        );
    };

    /**
     * watch the eventID and clear the messages for the old eventID
     */
    watch(eventID, (newid, oldid) => loadDismissedMessages(newid, oldid));

    watch(user, (newuser) => (newuser != undefined && newuser.ID != undefined && newuser?.ID > 0 ? loadDismissedMessages(0) : null));

    return {
        Dismiss: dismissMessage,

        DismissedMessages: computedDismissedMessages,
    };
});

export const useDismissedMessagesRef = () => storeToRefs(useDismissedMessages());
